import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { StyleSheet } from 'react-native';
import { isMovePayload } from '../../utils/recordPageLayoutModel';

const DragContext = createContext(null);

const HOLD_MS = 200;
const MOVE_THRESHOLD = 8;

function flattenStyle(style, extra) {
  return StyleSheet.flatten([style, extra].filter(Boolean));
}

function resolveDropZone(clientX, clientY) {
  if (typeof document === 'undefined') return null;
  const el = document.elementFromPoint(clientX, clientY);
  const zone = el?.closest?.('[data-rpl-drop]');
  if (!zone) return null;
  const kind = zone.getAttribute('data-rpl-drop');
  if (kind === 'empty') return { type: 'empty' };
  const rowId = zone.getAttribute('data-rpl-row');
  const col = zone.getAttribute('data-rpl-col');
  if (rowId == null || col == null) return null;
  return { type: 'column', rowId, columnIndex: parseInt(col, 10) };
}

function zonesMatch(a, b) {
  if (!a || !b) return false;
  if (a.type !== b.type) return false;
  if (a.type === 'empty') return true;
  return a.rowId === b.rowId && a.columnIndex === b.columnIndex;
}

function DragGhost({ x, y, label }) {
  return (
    <div
      style={{
        position: 'fixed',
        left: x + 12,
        top: y + 12,
        zIndex: 100000,
        pointerEvents: 'none',
        padding: '8px 14px',
        borderRadius: 6,
        backgroundColor: '#fffef8',
        border: '2px solid #d4af37',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        fontSize: 13,
        fontWeight: 600,
        color: '#333',
        maxWidth: 220,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </div>
  );
}

export function RecordLayoutDragProvider({ children, onDropEmpty, onDropColumn }) {
  const [session, setSession] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const onDropEmptyRef = useRef(onDropEmpty);
  const onDropColumnRef = useRef(onDropColumn);
  onDropEmptyRef.current = onDropEmpty;
  onDropColumnRef.current = onDropColumn;

  const draggingWidgetId = isMovePayload(session?.payload)
    ? session.payload._move.widgetId
    : null;

  const finishDrop = useCallback((clientX, clientY, payload) => {
    const zone = resolveDropZone(clientX, clientY);
    if (zone?.type === 'empty') {
      if (!isMovePayload(payload)) {
        onDropEmptyRef.current?.(payload);
      }
    } else if (zone?.type === 'column') {
      onDropColumnRef.current?.(zone.rowId, zone.columnIndex, payload);
    }
    setSession(null);
    setHighlight(null);
  }, []);

  const beginPointerDrag = useCallback(
    (payload, label, startX, startY, onPress) => {
      if (!payload?.type && !isMovePayload(payload)) return;

      let holdTimer = null;
      let dragging = false;
      const origin = { x: startX, y: startY };

      const activateDrag = () => {
        if (dragging) return;
        dragging = true;
        setSession({ payload, label, x: startX, y: startY, active: true });
      };

      const onMove = (e) => {
        const x = e.clientX;
        const y = e.clientY;
        if (!dragging) {
          const dist = Math.hypot(x - origin.x, y - origin.y);
          if (dist >= MOVE_THRESHOLD) {
            if (holdTimer) window.clearTimeout(holdTimer);
            activateDrag();
          }
        }
        if (dragging) {
          setSession((prev) => (prev ? { ...prev, x, y } : prev));
          setHighlight(resolveDropZone(x, y));
        }
      };

      const onUp = (e) => {
        if (holdTimer) window.clearTimeout(holdTimer);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);

        const x = e.clientX;
        const y = e.clientY;

        if (dragging) {
          finishDrop(x, y, payload);
        } else {
          const dist = Math.hypot(x - origin.x, y - origin.y);
          if (dist < MOVE_THRESHOLD) onPress?.();
        }
      };

      holdTimer = window.setTimeout(activateDrag, HOLD_MS);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [finishDrop]
  );

  return (
    <DragContext.Provider value={{ beginPointerDrag, highlight, draggingWidgetId }}>
      {children}
      {session?.active &&
        typeof document !== 'undefined' &&
        createPortal(
          <DragGhost x={session.x} y={session.y} label={session.label} />,
          document.body
        )}
    </DragContext.Provider>
  );
}

export function PaletteDraggable({
  payload,
  label,
  style,
  children,
  active,
  activeStyle,
  onPress,
}) {
  const ctx = useContext(DragContext);
  const displayLabel = label || payload?.label || payload?.type || 'Widget';

  const flatStyle = flattenStyle(style, [
    active && activeStyle,
    {
      cursor: 'grab',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      touchAction: 'none',
    },
  ]);

  return React.createElement(
    'div',
    {
      style: flatStyle,
      'data-rpl-palette': 'true',
      'data-record-layout-palette-item': 'true',
      onPointerDown: (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        ctx?.beginPointerDrag(payload, displayLabel, e.clientX, e.clientY, onPress);
      },
    },
    children
  );
}

export function CanvasWidgetDraggable({
  widget,
  rowId,
  columnIndex,
  label,
  style,
  children,
  onPress,
}) {
  const ctx = useContext(DragContext);
  const isDragging = ctx?.draggingWidgetId === widget?.id;
  const displayLabel = label || widget?.label || widget?.type || 'Widget';
  const payload = {
    ...widget,
    _move: { widgetId: widget.id, fromRowId: rowId, fromColumnIndex: columnIndex },
  };

  const flatStyle = flattenStyle(style, [
    {
      cursor: 'grab',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      touchAction: 'none',
      width: '100%',
      boxSizing: 'border-box',
      opacity: isDragging ? 0.35 : 1,
    },
  ]);

  return React.createElement(
    'div',
    {
      style: flatStyle,
      'data-rpl-canvas-widget': widget.id,
      'data-record-layout-widget': 'true',
      onPointerDown: (e) => {
        if (e.button !== 0) return;
        if (e.target?.closest?.('[data-rpl-widget-remove]')) return;
        e.preventDefault();
        e.stopPropagation();
        ctx?.beginPointerDrag(payload, displayLabel, e.clientX, e.clientY, onPress);
      },
    },
    children
  );
}

export function CanvasDropTarget({
  style,
  children,
  highlightStyle,
  dropRowId,
  dropColumnIndex,
  isEmptyZone,
  onPress,
}) {
  const ctx = useContext(DragContext);
  const myZone = isEmptyZone
    ? { type: 'empty' }
    : { type: 'column', rowId: dropRowId, columnIndex: dropColumnIndex };
  const isHighlighted = zonesMatch(ctx?.highlight, myZone);

  const dropAttrs = isEmptyZone
    ? { 'data-rpl-drop': 'empty' }
    : {
        'data-rpl-drop': 'column',
        'data-rpl-row': dropRowId,
        'data-rpl-col': String(dropColumnIndex),
      };

  const flatStyle = flattenStyle(style, [
    isHighlighted && (highlightStyle || {
      borderColor: '#d4af37',
      backgroundColor: 'rgba(212, 175, 55, 0.14)',
    }),
    { minHeight: 80, width: '100%', boxSizing: 'border-box' },
  ]);

  return React.createElement(
    'div',
    {
      style: flatStyle,
      'data-record-layout-drop-target': 'true',
      onClick: () => onPress?.(),
      ...dropAttrs,
    },
    children
  );
}

export function CanvasScrollDropBridge() {
  return null;
}
